import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { sendFindingsEmail, getMyEmails } from '../api/client'
import type { UserEmailOut } from '../api/users'
import type { ReconciliationSession } from '../types'
import {
  buildDraftBody,
  buildDraftSubject,
  computePartiesFilenames,
  STATUS_LABEL,
  OVERALL_LABEL,
} from '../utils/findingsEmailUtils'

interface Props {
  session: ReconciliationSession
  currentUserEmail?: string | null
  onSent: (to: string) => void
  onCancel: () => void
}

type Phase = 'compose' | 'sending' | 'done' | 'error'

const MANUAL_VALUE = '__manual__'

export function FindingsEmailModal({ session, currentUserEmail, onSent, onCancel }: Props) {
  const { t } = useTranslation()
  const [phase, setPhase] = useState<Phase>('compose')
  const [emailAddresses, setEmailAddresses] = useState<UserEmailOut[]>([])
  const [dropdownValue, setDropdownValue] = useState<string>('')
  const [manualAddress, setManualAddress] = useState(currentUserEmail ?? '')
  const toAddress = dropdownValue === MANUAL_VALUE ? manualAddress : (dropdownValue || (currentUserEmail ?? ''))

  const [subject, setSubject] = useState(() => buildDraftSubject(session))
  const [body, setBody] = useState(() => buildDraftBody(session))
  const [attachOrdrsp, setAttachOrdrsp] = useState(session.ordrsp_filename !== null)
  const [attachDesadv, setAttachDesadv] = useState(session.desadv_filename !== null)
  const [attachInvoice, setAttachInvoice] = useState(session.invoice_filename !== null)
  const [usePartiesNaming, setUsePartiesNaming] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  useEffect(() => {
    getMyEmails().then(list => {
      setEmailAddresses(list)
      const primary = list.find(e => e.is_primary) ?? list[0]
      setDropdownValue(primary?.email ?? '')
    }).catch(() => {})
  }, [])

  const partiesFilenames = computePartiesFilenames(session)
  const hasAttachment = attachOrdrsp || attachDesadv || attachInvoice
  const canSend = toAddress.trim().length > 0 && subject.trim().length > 0 && body.trim().length > 0

  const handleSend = async () => {
    setPhase('sending')
    try {
      const result = await sendFindingsEmail(session.id, {
        to_address: toAddress.trim(),
        subject: subject.trim(),
        body: body.trim(),
        attach_ordrsp: attachOrdrsp,
        attach_desadv: attachDesadv,
        attach_invoice: attachInvoice,
        use_parties_naming: usePartiesNaming,
      })
      if (result.sent) {
        setPhase('done')
      } else {
        setErrorMessage(t('findings_email_modal.send_failed_unknown'))
        setPhase('error')
      }
    } catch (e) {
      setErrorMessage(e instanceof Error ? e.message : t('findings_email_modal.send_failed'))
      setPhase('error')
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl mx-4 p-6 space-y-4 max-h-[92vh] overflow-y-auto">

        {phase === 'compose' && (
          <>
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{t('findings_email_modal.compose_title')}</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t('findings_email_modal.compose_description')}
            </p>

            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300">{t('findings_email_modal.to_label')}</label>
              {emailAddresses.length > 0 ? (
                <div className="space-y-1.5">
                  <select
                    value={dropdownValue}
                    onChange={e => setDropdownValue(e.target.value)}
                    className="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    {emailAddresses.map(e => (
                      <option key={e.id} value={e.email}>
                        {e.email}{e.label ? ` (${e.label})` : ''}{e.is_primary ? ' ★' : ''}
                      </option>
                    ))}
                    <option value={MANUAL_VALUE}>{t('findings_email_modal.other_address_option')}</option>
                  </select>
                  {dropdownValue === MANUAL_VALUE && (
                    <input
                      type="email"
                      value={manualAddress}
                      onChange={e => setManualAddress(e.target.value)}
                      placeholder={t('findings_email_modal.email_placeholder')}
                      autoFocus
                      className="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  )}
                </div>
              ) : (
                <input
                  type="email"
                  value={manualAddress}
                  onChange={e => setManualAddress(e.target.value)}
                  placeholder={t('findings_email_modal.email_placeholder')}
                  className="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              )}
            </div>

            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300">{t('findings_email_modal.subject_label')}</label>
              <input
                type="text"
                value={subject}
                onChange={e => setSubject(e.target.value)}
                className="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300">{t('findings_email_modal.body_label')}</label>
              <textarea
                value={body}
                onChange={e => setBody(e.target.value)}
                rows={16}
                className="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-xs font-mono text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-y"
              />
            </div>

            <div className="space-y-2">
              <p className="text-xs font-medium text-gray-700 dark:text-gray-300">{t('findings_email_modal.attachments_heading')}</p>
              <div className="space-y-1.5">
                {session.ordrsp_filename && (
                  <AttachmentCheckbox
                    checked={attachOrdrsp}
                    onChange={setAttachOrdrsp}
                    label="OrderResponse"
                    filename={session.ordrsp_filename}
                    partiesFilename={partiesFilenames.order_response}
                    usePartiesNaming={usePartiesNaming}
                  />
                )}
                {session.desadv_filename && (
                  <AttachmentCheckbox
                    checked={attachDesadv}
                    onChange={setAttachDesadv}
                    label="DespatchAdvice"
                    filename={session.desadv_filename}
                    partiesFilename={partiesFilenames.despatch_advice}
                    usePartiesNaming={usePartiesNaming}
                  />
                )}
                {session.invoice_filename && (
                  <AttachmentCheckbox
                    checked={attachInvoice}
                    onChange={setAttachInvoice}
                    label="Factuur"
                    filename={session.invoice_filename}
                    partiesFilename={partiesFilenames.invoice}
                    usePartiesNaming={usePartiesNaming}
                  />
                )}
              </div>

              <label className="flex items-center gap-2.5 cursor-pointer select-none py-1.5 px-3 rounded-lg border border-dashed border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors">
                <input
                  type="checkbox"
                  checked={usePartiesNaming}
                  onChange={e => setUsePartiesNaming(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                />
                <span className="text-xs text-gray-700 dark:text-gray-300">
                  {t('findings_email_modal.rename_parties_label')}
                  <span className="ml-1.5 text-gray-400 dark:text-gray-500">{t('findings_email_modal.rename_parties_hint')}</span>
                </span>
              </label>

              {!hasAttachment && (
                <p className="text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800/50 rounded-lg px-3 py-2">
                  {t('findings_email_modal.no_attachments_warning')}
                </p>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <button onClick={onCancel} className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">{t('findings_email_modal.cancel_button')}</button>
              <button onClick={handleSend} disabled={!canSend} className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">{t('findings_email_modal.send_button')}</button>
            </div>
          </>
        )}

        {phase === 'sending' && (
          <>
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{t('findings_email_modal.sending_title')}</h2>
            <div className="flex items-center gap-3 py-4">
              <svg className="w-5 h-5 text-teal-600 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <span className="text-sm text-gray-600 dark:text-gray-400">{t('findings_email_modal.sending_status', { toAddress })}</span>
            </div>
          </>
        )}

        {phase === 'done' && (
          <>
            <div className="flex items-center gap-3">
              <span className="flex-shrink-0 w-8 h-8 rounded-full bg-green-100 text-green-600 flex items-center justify-center">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </span>
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{t('findings_email_modal.done_title')}</h2>
            </div>
            <p className="text-sm text-gray-500">{t('findings_email_modal.done_description', { toAddress })}</p>
            <div className="flex justify-end pt-1">
              <button onClick={() => onSent(toAddress)} className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors">{t('findings_email_modal.close_button')}</button>
            </div>
          </>
        )}

        {phase === 'error' && (
          <>
            <div className="flex items-center gap-3">
              <span className="flex-shrink-0 w-8 h-8 rounded-full bg-red-100 text-red-600 flex items-center justify-center">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </span>
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{t('findings_email_modal.error_title')}</h2>
            </div>
            <p className="text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800/50 rounded-lg px-3 py-2">{errorMessage}</p>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setPhase('compose')} className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">{t('findings_email_modal.back_button')}</button>
              <button onClick={onCancel} className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">{t('findings_email_modal.close_button')}</button>
            </div>
          </>
        )}

      </div>
    </div>
  )
}

// ── AttachmentCheckbox: herbruikbaar bijlagen-rij ────────────────────────────

interface AttachmentCheckboxProps {
  checked: boolean
  onChange: (v: boolean) => void
  label: string
  filename: string
  partiesFilename: string
  usePartiesNaming: boolean
}

function AttachmentCheckbox({ checked, onChange, label, filename, partiesFilename, usePartiesNaming }: AttachmentCheckboxProps) {
  return (
    <label className="flex items-center gap-2.5 cursor-pointer select-none py-1.5 px-3 rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-100 dark:border-gray-800">
      <input
        type="checkbox"
        checked={checked}
        onChange={e => onChange(e.target.checked)}
        className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
      />
      <span className="text-xs text-gray-700 dark:text-gray-300 min-w-0">
        <span className="font-medium">{label}</span>
        {usePartiesNaming ? (
          <span className="ml-1.5 font-mono text-teal-600 dark:text-teal-400 block truncate">{partiesFilename}</span>
        ) : (
          <span className="ml-1.5 font-mono text-gray-400 dark:text-gray-500">{filename}</span>
        )}
      </span>
    </label>
  )
}

// Re-export labels zodat andere componenten ze kunnen gebruiken zonder de utils te importeren
export { STATUS_LABEL, OVERALL_LABEL }
